/**
 * This file having the resuable api calls for GET, PUT, POST, DELETE.
 * getToken function will check whether the user is valid or not.
 * The functions will be used as follows.
 * GET: await getRequest(url)
 * POST: await postRequest(url) or await postRequest(url, JSON.stringify(body));
 * PUT: await putRequest(url) or await putRequest(url, JSON.stringify(body));
 * DELETE: await deleteRequest(url) or await deleteRequest(url, JSON.stringify(body));
 */

import { session } from '../@mojang/auth';
import { AnalyticsService } from 'minecraft-net-core-services';
import { IRequest } from './Interfaces/Utils.interface';
import { APPLICATION_PROFILE, StatusCode, toastIcons } from '../Constants/Constants';
import * as Cookies from 'es-cookie';
/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
import { logout } from './Utils';
import { notify } from '../CoreUI/ToastNotification/ToastNotification';
import { IToastType } from '../CoreUI/ToastNotification/ToastNotification.constants';
import globalStore from '../Stores/GlobalStore';

const getToken = () => {
  return `Bearer ${session.getToken()}`;
};

// Check for MCSTO cookie
const mcsto = Cookies.get('mcsto-preauth');

const headersObj = {
  ...(mcsto && { 'X-WEB-PREAUTH-SECRET': mcsto }),
  Authorization: getToken()
};

const headersObjNoAuth = {
  ...(mcsto && { 'X-WEB-PREAUTH-SECRET': mcsto })
};

/**
 * It will help to call the GET api with the following url.
 * @param requestData The data for the get request
 * @returns Promise
 */
export function getRequest(requestData: Omit<IRequest, 'type' | 'payload'>) {
  return request({ ...requestData, type: 'GET' });
}

/**
 * We are using this function specifically, not to send the Authorization Token in header.
 * @param requestData The data for the post request
 * @returns Promise
 */
export function postRequestWithoutAuthHeader(requestData: Omit<IRequest, 'type'>) {
  return request({ ...requestData, type: 'POST' });
}

/**
 * It will help to call the POST api with the following url.
 * @param requestData The data for the post request
 * @returns Promise
 */
export function postRequest(requestData: Omit<IRequest, 'type'>) {
  return request({ ...requestData, type: 'POST' });
}
/**
 * It will help to call the PUT api with the following url.
 * @param requestData The data for the put request
 * @returns Promise
 */
export function putRequest(requestData: Omit<IRequest, 'type'>) {
  return request({ ...requestData, type: 'PUT' });
}
/**
 * It will help to call the DELETE api with the following url.
 * @param requestData The data for the delete request
 * @returns Promise
 */
export function deleteRequest(requestData: Omit<IRequest, 'type'>) {
  return request({ ...requestData, type: 'DELETE' });
}

/**
 *
 * @param type of API Method (GET | POST | PUT | Delete)
 * @param url of the request
 * @param payload of the request
 * @param headers added to the request
 * @param credentials added to the request
 * @param showDefaultErrorToast true if we should show the toast on error
 * @param serviceName used for telemetry
 * @returns Promise
 */
/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
export async function request({ type, url, payload, headers, discardAuthHeader, credentials, showDefaultErrorToast, serviceName }: IRequest) {
  const createdHeaderObj = discardAuthHeader ? headersObjNoAuth : headersObj;
  // request start time
  const requestStartTime = Date.now();
  const requestBody: any = {
    method: type,
    headers: headers ? Object.assign({}, createdHeaderObj, headers) : createdHeaderObj
  };
  if (credentials) {
    requestBody.credentials = credentials;
  }
  if (payload) {
    requestBody.body = payload;
  }
  try {
    const response = await fetch(url, requestBody);

    if (response?.url) {
      const { trackServiceApi } = AnalyticsService;
      trackServiceApi({
        application: APPLICATION_PROFILE,
        apiUrl: response.url,
        endpointName: serviceName || '',
        method: type,
        httpStatusCode: response.status,
        responseTimeMs: Date.now() - requestStartTime
      });
    }
    if (response.status === StatusCode.NoContent) {
      return { data: response, error: false };
    }
    if (response.status === StatusCode.UnAuthorized) {
      logout(true);
    }
    if (showDefaultErrorToast && response.status >= StatusCode.InternalServerError && response.status <= StatusCode.NetworkAuthenticationRequired) {
      notify(toastIcons.Warning, globalStore.apiFailureToastMessage, true, IToastType.warning);
    }
    if (response.ok) {
      return { data: await response.json(), error: false };
    } else {
      return { data: response, error: true };
    }
  } catch (error) {
    return { data: null, error: true };
  }
}
